// Importing Constant
import { APPLE_PAY_CARD_CONVINIENCE_FEES, PAYMENT_TYPES } from 'portal-modules/payments/constants'
import { getEmbeddedViewFlag } from './get-embedded-flag'
import getPaymentSourceCondition from './get-payment-source-condition'

// Get payment source condition to check if it's a Colleen payment
const { isColleenPayment } = getPaymentSourceCondition()

/* eslint-disable no-undef */
/**
 * Function to create an Apple Pay Button
 * @param {Function} onClickHandler - The event handler for the button click event
 * @param {string} type - The default Apple pay button type
 * @param {string} buttonStyle - The style of the Apple Pay button (default: 'black', options: 'black', 'white', 'white-outline')
 * @returns {HTMLElement} - The created Apple Pay button element
 */
export const createApplePayButton = (type = 'plain', buttonStyle = 'black') => {
  // Create the Apple Pay button element
  const applePayButton = document.createElement('apple-pay-button')

  // Define required attributes for the Apple Pay button
  const attributes = [
    { name: 'buttonstyle', value: buttonStyle }, // Button style: 'black', 'white', or 'white-outline'
    {
      name: 'type',
      value: type,
    }, // Button type: 'plain', 'add-money', 'book', 'buy', 'check-out', 'continue', 'contribute',
    // 'donate', 'order', 'pay', 'reload', 'rent', 'set-up', 'subscribe', 'support', 'tip', 'top-up'
    {
      name: 'locale',
      value: i18nUtils.getLocale(),
    }, // Locale: e.g., 'en-US', 'ar-AB', 'ca-ES', 'zh-CN', 'zh-HK', 'zh-TW', 'hr-HR', 'cs-CZ', 'da-DK',
    // 'de-DE', 'nl-NL', 'en-AU', 'en-GB', 'fi-FI', 'fr-CA', 'fr-FR', 'el-GR', 'he-IL', 'hi-IN',
    // 'hu-HU', 'id-ID', 'it-IT', 'ja-JP', 'ko-KR', 'ms-MY', 'nb-NO', 'pl-PL', 'pt-BR', 'pt-PT',
    // 'ro-RO', 'ru-RU', 'sk-SK', 'es-MX', 'es-ES', 'sv-SE', 'th-TH', 'tr-TR', 'uk-UA', 'vi-VN'
  ]

  // Set attributes dynamically based on the attributes array
  attributes.forEach(({ name, value }) => {
    applePayButton.setAttribute(name, value)
  })
  // Return the created Apple Pay button element
  return applePayButton
}

/**
 * Compare 2 objects
 */
export const deepEqualObject = (obj1, obj2) => {
  // Check for strict equality or if both are null
  if (obj1 === obj2) {
    return true
  }

  // Check for non-object types or null values
  if (
    obj1 === null ||
    obj2 === null ||
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object'
  ) {
    return false
  }

  // Get the keys of both objects
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  // Check if the number of keys is different
  if (keys1.length !== keys2.length) {
    return false
  }

  // Use a single loop to compare keys and values
  for (let key of keys1) {
    if (!obj2.hasOwnProperty(key) || !deepEqualObject(obj1[key], obj2[key])) {
      return false
    }
  }

  return true
}

/**
 * Sets a cookie with a specified name and value, with a lifetime of 10 years.
 *
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to be stored in the cookie.
 */
export const setCookie = (name, value) => {
  const date = new Date()
  date.setFullYear(date.getFullYear() + 10) // Set cookie to expire in 10 years
  const expires = '; expires=' + date.toUTCString()
  document.cookie = name + '=' + (value || '') + expires + '; path=/'
}

/**
 * Retrieves the value of a cookie with a specified name.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} - The value of the cookie if found, or null if not found.
 */
export const getCookie = name => {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length) // Trim leading whitespace
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length) // Return cookie value if name matches
  }
  return null // Return null if cookie not found
}

/**
 * Erases a cookie with a specified name.
 *
 * @param {string} name - The name of the cookie to erase.
 */
export const deleteCookie = name => {
  document.cookie = name + '=; Max-Age=-99999999;' // Set cookie's Max-Age to a past date to delete it
}

/**
 * Checks if the provided object is not empty.
 * @param {any} obj - The object to check.
 * @returns {boolean} Returns true if the object is not empty, false otherwise.
 */
export const isEmptyObject = obj => {
  // Check if obj is an array and has elements
  if (Array.isArray(obj)) {
    return obj.length > 0
  } else if (typeof obj === 'object' && obj !== null) {
    // Check if obj is a non-null object and has keys
    return Object.keys(obj).length > 0
  }
  // Return false for all other types (including null, undefined, etc.)
  return false
}

/**
 * Utility function to check if Apple Pay is available on the device.
 *
 * @param {string} merchantIdentifier - The merchant identifier to check for Apple Pay compatibility.
 * @returns {boolean} - Returns true if Apple Pay is available, false otherwise.
 */
export const isApplePayAvailable = () => {
  const isEmbedded = getEmbeddedViewFlag();
  //condition to hide apple pay in embedded view
  if(isEmbedded && !isColleenPayment){
    return false
  }
  // Check if the ApplePaySession object is available in the window (indicating Apple Pay support)
  if (
    !window?.ApplePaySession ||
    !ApplePaySession?.canMakePayments()
  ) {
    // Log an error message if Apple Pay is not available on this device
    console.warn('Apple Pay is not available on this device.')
    // Return false indicating that Apple Pay is not available
    return false
  }
  // Return true indicating that Apple Pay is available
  return true
}

/**
 * Function to get the convenience fee for Apple Pay based on the card type.
 *
 * @param {string} type - The type of card ("CREDIT" or "DEBIT").
 * @param {object} applePayMethod - The Apple Pay method object containing fee information.
 * @param {object} APPLE_PAY_CARD_CONVINIENCE_FEES - The default convenience fees for Apple Pay.
 * @returns {number} - The convenience fee for the specified card type.
 */

export const getApplePayConvenienceFee = (type, applePayMethod) => {
  // Retrieve the fees from applePayMethod or default to an empty object if not defined
  const fees = applePayMethod?.fees || {}

  // Convert the card type to lowercase to match the keys in the fees object
  const feeType = type.toLowerCase()

  // Map the fee type, applying the debit fee to 'prepaid' card types
  const mappedFeeType = feeType === 'prepaid' ? 'debit' : feeType

  // Get the default fee for the specified card type
  const defaultFee = APPLE_PAY_CARD_CONVINIENCE_FEES[mappedFeeType] || 0

  // Return the fee from the fees object if it exists, otherwise return the default fee
  return fees[mappedFeeType] !== undefined ? fees[mappedFeeType] : defaultFee
}

/**
 * Transforms the Apple Pay billingContact object into the desired bill_to_customer format.
 * 
 * @param {Object} billingContact - The billing contact information provided by Apple Pay.
 * @returns {Object|null} - Returns the transformed bill_to_customer object or null if billingContact is not provided.
 */
export const transformBillingContact = (billingContact) => {
  if (!billingContact) return null; // Return null if billingContact is not present

  const {
    givenName: first_name = '',
    familyName: last_name = '',
    postalCode: postal_code = '',
    countryCode: country_code = '',
    email = '',
    locality: city = '',
    administrativeArea: state = '',
    addressLines = []
  } = billingContact;

  // Combine address lines into a single string
  const address_line_1 = addressLines.join(', ');

  return {
    first_name,
    last_name,
    postal_code,
    country_code,
    email,
    city,
    state,
    address_line_1
  };
};

/**
 * Logs messages to the console only if the 'applepay_console_log' cookie is set.
 *
 * @param {...any} args - The messages or variables to log. Accepts multiple arguments like console.log.
 */
export const applePayConsoleLog = (...args) => {
  // Check if the 'applepay_console_log' cookie is set
  if (getCookie('applepay_console_log')) {
      // If the cookie exists, log the arguments to the console
      console.log(...args);
  }
}

/**
 * Used to place apple pay payment method before moneygram if both payment methods are avaialble
 * 
 * @param {Array} paymentMethods - Array of all payment methods available
 * @returns {Array} - Returns the array where apple pay is sorted and placed before moneygram.
 */
export const sortApplePayBeforeMoneygram = (paymentMethods) => {
  const applePayPaymentMethod = paymentMethods.findIndex(item => item.payment_type_id === PAYMENT_TYPES.APPLE_PAY.value);
  const moneygramPaymentMethod = paymentMethods.findIndex(item => item.payment_type_id === PAYMENT_TYPES.MONEYGRAM.value);

  // Check if both Apple Pay and Moneygram are present
  if (applePayPaymentMethod !== -1 && moneygramPaymentMethod !== -1) {
    // Remove both Apple Pay and Moneygram from their current positions
    const [applePay] = paymentMethods.splice(applePayPaymentMethod, 1);
    const [moneygram] = paymentMethods.splice(moneygramPaymentMethod, 1);

    // Push Apple Pay to the second last position
    paymentMethods.splice(paymentMethods.length, 0, applePay);  // Add to second last position
    // Push Moneygram to the last position
    paymentMethods.push(moneygram);  // Add to last position
  }

  return paymentMethods;
}

export const calculateIfValidateShouldBeCalled = (payment_amount, entered_payment_amount, editRewardPoints, rewardAmountApplied) => {
  if(editRewardPoints && payment_amount == 0 && rewardAmountApplied == entered_payment_amount){
    return false
  }
  return true
}
